import type { I3EventsPageCms, I3ServicesCms, I3SiteContentV1 } from './types';

/**
 * Default Services — Lisa Norman therapy offerings.
 * Booking URLs live in `data/serviceBookingLinks.ts` (Virtual / In-Person per service).
 */
export const DEFAULT_SERVICES_CMS: I3ServicesCms = {
  pageSubtitle:
    'Counseling for individuals, couples, and families—read each offering and book the path that fits.',
  pillars: [
    {
      id: 'marriage-enrichment',
      title: 'Counseling & therapy services',
      subtitle:
        'Marriage and mental health care in one practice—so your relationships and your inner life both get attention.',
      hidePillarHeading: true,
      cards: [
        {
          id: 'premarital-counseling',
          title: 'Pre-marital counseling',
          whoItsFor: 'Engaged couples preparing for marriage',
          summary:
            'Prepare for marriage with conversations about expectations, communication, conflict, family systems, and shared faith and values.',
          outcomes: ['Shared expectations', 'Healthy conflict skills', 'A foundation before the wedding'],
          detailHtml: `<p class="i3-modal-lead">I help engaged couples move past the checklist and into honest dialogue—so you enter marriage as partners who know how to repair, apologize, and choose each other on hard days.</p>
<section class="i3-modal-section" aria-label="What to expect"><h3 class="i3-modal-section-title">What to expect</h3><ul class="i3-modal-list"><li>Sessions shaped around your timeline before the wedding</li><li>Tools for listening, repair, and decision-making together</li><li>Optional coordination with your officiant or premarital requirements</li></ul></section>`,
          bookUrl: '',
        },
        {
          id: 'postmarital-counseling',
          title: 'Marriage & couples counseling',
          whoItsFor: 'Married or committed couples navigating strain, disconnection, or healing',
          summary:
            'Therapy for recurring conflict, trust repair, intimacy concerns, life transitions, and “we feel like roommates” seasons.',
          outcomes: ['Safer conversations', 'Clearer patterns', 'A path toward reconnection'],
          detailHtml: `<p class="i3-modal-lead">Couples therapy is for more than crisis—it’s for patterns that won’t change on their own. We slow the cycle, name what’s underneath it, and practice new ways of turning toward each other.</p>
<section class="i3-modal-section" aria-label="Good fit if"><h3 class="i3-modal-section-title">Good fit if</h3><ul class="i3-modal-list"><li>The same argument keeps returning</li><li>Trust or intimacy has been wounded</li><li>You want professional help before a big decision</li></ul></section>`,
          bookUrl: '',
        },
        {
          id: 'counseling-1on1',
          title: 'Individual therapy',
          whoItsFor: 'Adults seeking one-on-one mental health support',
          summary:
            'Confidential therapy for anxiety, depression, stress, grief, burnout, identity, and major life transitions.',
          outcomes: ['A steady therapeutic relationship', 'Skills that work outside session', 'Care aligned with your values'],
          detailHtml: `<p class="i3-modal-lead">Individual therapy gives you room to be honest about what you’re carrying—without performing. We work at your pace, with approaches that fit anxiety, mood, stress, and the stories that shaped you.</p>
<section class="i3-modal-section" aria-label="Topics"><h3 class="i3-modal-section-title">Common topics</h3><ul class="i3-modal-list"><li>Anxiety, panic, or feeling “on” all the time</li><li>Depression, fatigue, or emotional numbness</li><li>Grief, burnout, or big life change</li><li>Preparing for hard conversations in your marriage or family</li></ul></section>`,
          bookUrl: '',
        },
        {
          id: 'family-counseling',
          title: 'Family counseling',
          whoItsFor: 'Parents, co-parents, and families navigating conflict or transitions',
          summary:
            'Family sessions for parenting alignment, blended families, teen conflict, and stress that affects everyone at home.',
          outcomes: ['Clearer roles and boundaries', 'Calmer conflict', 'More secure connection at home'],
          detailHtml: `<p class="i3-modal-lead">Family therapy helps you step out of the same fight on repeat. We build communication that fits your children’s ages and your family’s reality—including blended homes and co-parenting when needed.</p>
<section class="i3-modal-section" aria-label="Focus areas"><h3 class="i3-modal-section-title">Focus areas</h3><ul class="i3-modal-list"><li>Parenting stress and discipline alignment</li><li>Blended-family and loyalty concerns</li><li>Teen or young-adult conflict</li><li>Healing after separation, moves, or trauma</li></ul></section>`,
          bookUrl: '',
        },
        {
          id: 'speaking-engagements',
          title: 'Workshops & speaking',
          whoItsFor: 'Churches, retreats, and groups',
          summary:
            'Premarital workshops, marriage enrichment, and presentations on emotional health and strong families.',
          outcomes: ['Clear teaching', 'Engaged participants', 'Practical takeaways'],
          detailHtml: `<p class="i3-modal-lead">Invite me to bring clinical insight and compassionate teaching to your event—keynotes, breakouts, or half-day workshops tailored to your audience.</p>
<section class="i3-modal-section" aria-label="Typical topics"><h3 class="i3-modal-section-title">Typical topics</h3><ul class="i3-modal-list"><li>Communication and repair in marriage</li><li>Anxiety, stress, and healthy families</li><li>Faith-informed emotional wellness</li></ul></section>
<p>Reach out with your date, venue, and audience; I’ll follow up with availability and next steps.</p>`,
          bookUrl: '',
        },
      ],
    },
  ],
};

/** Events page — edit when you publish real dates and media. */
export const DEFAULT_EVENTS_PAGE: I3EventsPageCms = {
  pageSubtitle: 'Workshops, retreats, and gatherings hosted or facilitated by Lisa.',
  latestHighlight: {
    id: 'fanning-the-flame-latest',
    label: 'Latest experience',
    title: 'Fanning the Flame Marriage Retreat',
    dateLine: 'March 2026 · Indianapolis',
    descriptionHtml: `<p>This retreat brought couples together for teaching, reflection, and community. Replace photos and copy with your real event assets when ready.</p>
<p>Announcements can also live on the <a href="/blog">Journal</a>.</p>`,
    carouselSlides: [
      {
        type: 'image',
        src: '/lisa/placeholders/event-carousel-workshop.svg',
        caption: 'Placeholder — replace with workshop / room photo.',
        title: 'Workshop in session (placeholder)',
      },
      {
        type: 'image',
        src: '/lisa/placeholders/event-carousel-celebration.svg',
        caption: 'Placeholder — replace with celebration / group moment.',
        title: 'Event moment (placeholder)',
      },
    ],
    videos: [],
    photos: [],
  },
  upcomingSectionTitle: 'Up next',
  upcomingEvents: [],
  upcomingEmptyHtml: `<p><strong>No upcoming dates listed yet.</strong> When you announce your next workshop or retreat, it will appear here—reach out if you’d like help publishing it.</p>
<p>You can also follow the <a href="/blog">Journal</a> for encouragement and updates.</p>`,
  homeSpotlight: {
    image: '/lisa/placeholders/spotlight-home.svg',
    headline: 'From our latest retreat',
    subline:
      'A snapshot from Fanning the Flame — longer reflections and updates live on the Journal.',
    linkLabel: 'Read the Journal',
    linkTo: '/blog',
  },
};

/** Embedded site content (no CMS fetch by default). */
export const DEFAULT_SITE_CONTENT: I3SiteContentV1 = {
  version: '1',
  updatedAt: '2026-03-16T00:00:00.000Z',
  featuredEvent: {
    id: 'fanning-the-flame-2026',
    label: 'retreat',
    title: 'Fanning the Flame Marriage Retreat',
    year: '2026',
    date: 'Saturday, March 7, 2026',
    time: '9:00am – 4:30pm',
    checkIn: 'Check-in: 8:30am',
    location: '777 S. White River Pkwy Dr., Indianapolis, IN 46221',
    description:
      'A day for couples to reconnect—with teaching, reflection, and practical tools. Facilitated by Lisa Norman.',
    registerTypeformUrl: '',
    payStripeUrl: '',
    heroImage: undefined,
  },
  eventsPage: DEFAULT_EVENTS_PAGE,
  moreEventsCopy:
    'Additional workshops and retreats will be listed here. Check back or reach out to be notified.',
  pastEventsIntro:
    'Highlights from past retreats and workshops. Click any image or video to view fullscreen.',
  pastEvents: [
    {
      id: '1',
      title: 'Fanning the Flame Marriage Retreat',
      description:
        'Couples gathered for teaching, guided reflection, and community—focused on communication and connection.',
      date: 'Mar 2026',
      media: [],
    },
    {
      id: '2',
      title: 'Family & parenting evening',
      description:
        'A focused evening for parents on calm leadership at home, boundaries, and teamwork between caregivers.',
      date: 'TBD',
      media: [],
    },
  ],
  services: DEFAULT_SERVICES_CMS,
  blogPosts: undefined,
  operations: {
    headline: 'The Bridge- Wellness & Therapy Center',
    featuredEventRegistrationCount: 0,
    totalInquiriesCount: 0,
    notes: 'Local demo data — edit in defaultSiteContent.ts.',
  },
};
